import { useState } from "react";

/**
 * Bildergalerie im 3er-Grid: die ersten 3 Bilder werden angezeigt,
 * bei mehr als 3 Bildern zeigt das letzte sichtbare Bild ein "+X"-Overlay.
 * Klick auf ein Bild öffnet eine Lightbox zum Durchblättern aller Bilder.
 */
export default function NewsGallery({ bilder }) {
  const [openIndex, setOpenIndex] = useState(null);

  if (!bilder || bilder.length === 0) return null;

  const VISIBLE = 3;
  const sichtbar = bilder.slice(0, VISIBLE);
  const rest = bilder.length - VISIBLE;

  function next(e) {
    e?.stopPropagation();
    setOpenIndex((i) => (i + 1) % bilder.length);
  }
  function prev(e) {
    e?.stopPropagation();
    setOpenIndex((i) => (i - 1 + bilder.length) % bilder.length);
  }

  return (
    <div className="mt-12">
      <p className="text-xs font-bold uppercase tracking-widest text-dark/40 mb-4">
        Bildergalerie ({bilder.length} {bilder.length === 1 ? "Bild" : "Bilder"})
      </p>

      <div className="grid grid-cols-3 gap-2">
        {sichtbar.map((bild, i) => {
          const isLastVisible = i === VISIBLE - 1 && rest > 0;
          return (
            <button
              key={i}
              type="button"
              onClick={() => setOpenIndex(i)}
              className="relative aspect-square overflow-hidden bg-dark/5 group"
            >
              <img
                src={bild}
                alt={`Bild ${i + 1}`}
                loading="lazy"
                decoding="async"
                className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              {isLastVisible && (
                <div className="absolute inset-0 bg-dark/60 flex items-center justify-center">
                  <span className="text-white text-xl md:text-2xl font-extrabold">
                    +{rest}
                  </span>
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Lightbox */}
      {openIndex !== null && (
        <div
          className="fixed inset-0 z-50 bg-dark/95 flex items-center justify-center p-4 md:p-10"
          onClick={() => setOpenIndex(null)}
        >
          <button
            onClick={() => setOpenIndex(null)}
            className="absolute top-4 right-4 text-white/80 hover:text-white p-2"
            aria-label="Schliessen"
          >
            <svg className="h-7 w-7" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <button
            onClick={prev}
            className="absolute left-2 md:left-6 text-white/70 hover:text-white p-2"
            aria-label="Vorheriges Bild"
          >
            <svg className="h-8 w-8 md:h-10 md:w-10" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          <img
            src={bilder[openIndex]}
            alt={`Bild ${openIndex + 1}`}
            onClick={(e) => e.stopPropagation()}
            className="max-h-[85vh] max-w-[85vw] object-contain"
          />

          <button
            onClick={next}
            className="absolute right-2 md:right-6 text-white/70 hover:text-white p-2"
            aria-label="Nächstes Bild"
          >
            <svg className="h-8 w-8 md:h-10 md:w-10" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </button>

          <span className="absolute bottom-4 md:bottom-6 text-white/60 text-sm">
            {openIndex + 1} / {bilder.length}
          </span>
        </div>
      )}
    </div>
  );
}
